import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { Zap, Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function LoginPage() {
  const [step, setStep] = useState("login"); // "login" | "totp"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Kullanıcı adı ve şifre gerekli");
      return;
    }
    setLoading(true);
    try {
      const res = await login(username, password);
      // Normal login (no 2FA)
      navigate("/");
    } catch (err) {
      const data = err.response?.data;
      if (data?.requires_2fa) {
        // 2FA required — store temp token and show TOTP step
        setTempToken(data.temp_token);
        setStep("totp");
        toast.info("Google Authenticator kodunu girin");
      } else {
        toast.error(data?.detail || "Giriş başarısız");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    if (totpCode.length !== 6) { toast.error("6 haneli kod girin"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${window.location.origin}/api/auth/verify-totp`, {
        temp_token: tempToken, code: totpCode,
      });
      // Manually set auth since we bypassed the normal login flow
      localStorage.setItem("token", res.data.token);
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Geçersiz kod");
      setTotpCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Form */}
      <div className="w-full lg:w-[480px] flex flex-col justify-center px-10 py-12 bg-white">
        <div className="max-w-sm mx-auto w-full">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-[#DC2626] rounded-md flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <div
                className="font-bold uppercase text-gray-900 tracking-widest text-lg leading-none"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}
              >
                UÇAR
              </div>
              <div className="text-xs text-gray-400 uppercase tracking-widest">Price API System</div>
            </div>
          </div>

          <h1
            className="text-4xl font-bold text-gray-900 mb-2 uppercase tracking-tight"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            Yönetici Girişi
          </h1>
          <p className="text-sm text-gray-500 mb-8">
            Fiyat listesi yönetim paneline erişmek için giriş yapın.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1.5">
                Kullanıcı Adı
              </label>
              <input
                data-testid="login-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent transition-all"
                placeholder="admin"
                autoComplete="username"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1.5">
                Şifre
              </label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent transition-all pr-10"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold uppercase tracking-wider rounded-md text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98]"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>
          </form>

          {step === "totp" && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 animate-fade-in-up">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-green-50 border-2 border-green-200 rounded-full flex items-center justify-center mx-auto mb-3">
                    <ShieldCheck className="w-7 h-7 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 uppercase"
                    style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                    2 Adımlı Doğrulama
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Google Authenticator uygulamasındaki <br />
                    <strong>Uçar Price API</strong> kodunu girin
                  </p>
                </div>

                <form onSubmit={handleTotpSubmit} data-testid="totp-form">
                  <input
                    data-testid="totp-code-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={totpCode}
                    onChange={e => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    autoFocus
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl text-3xl font-mono text-center tracking-widest focus:outline-none focus:border-[#DC2626] transition-all"
                  />
                  <button
                    data-testid="totp-submit-btn"
                    type="submit"
                    disabled={loading || totpCode.length !== 6}
                    className="w-full mt-4 py-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold uppercase rounded-lg text-sm transition-all disabled:opacity-50"
                    style={{ fontFamily: "Barlow Condensed, sans-serif" }}
                  >
                    {loading ? "Doğrulanıyor..." : "Doğrula →"}
                  </button>
                  <button type="button" onClick={() => { setStep("login"); setTotpCode(""); setTempToken(""); }}
                    className="w-full mt-2 text-xs text-gray-400 hover:text-gray-600 py-1">
                    ← Geri dön
                  </button>
                </form>
              </div>
            </div>
          )}

          <div className="mt-6 p-3 bg-gray-50 rounded-md border border-gray-100">
            <p className="text-xs text-gray-400 text-center">Şifrenizi unuttuysanız sistem yöneticinize başvurun.</p>
          </div>
        </div>
      </div>

      {/* Right - Image */}
      <div
        className="hidden lg:flex flex-1 items-end p-12"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1762618621440-7082e14bc8c0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="bg-black/60 backdrop-blur-sm rounded-lg p-6 max-w-sm">
          <p
            className="text-white text-2xl font-bold uppercase tracking-wide leading-tight"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            Merkezi Fiyat Yönetimi
          </p>
          <p className="text-gray-300 text-sm mt-2">
            Kommo CRM entegrasyonu için anlık fiyat sorgulama sistemi.
          </p>
        </div>
      </div>
    </div>
  );
}
